import React, { useState } from 'react';
import { Loader2, Send, CheckCircle, XCircle, Clock, ShieldCheck, ShieldX } from 'lucide-react';
import { toast } from 'sonner';
import authedFetch from '../../lib/authedFetch';
import secureStorage from '../../lib/secureStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Small status chip — "Draft", "Pending approval", "Approved", "Rejected"
 * used both in the grid tile and the BCB hero.
 */
export function ApprovalStatusChip({ status, size = 'sm', testId }) {
  const s = (status || 'draft').toLowerCase();
  const map = {
    draft: {
      label: 'Draft',
      cls: 'bg-slate-100 text-slate-600',
      Icon: Clock,
    },
    submitted: {
      label: 'Pending approval',
      cls: 'bg-amber-100 text-amber-800',
      Icon: Clock,
    },
    approved: {
      label: 'Approved',
      cls: 'bg-emerald-100 text-emerald-800',
      Icon: ShieldCheck,
    },
    rejected: {
      label: 'Rejected',
      cls: 'bg-rose-100 text-rose-800',
      Icon: ShieldX,
    },
  };
  const cfg = map[s] || map.draft;
  const padding = size === 'sm' ? 'px-1.5 py-0.5' : 'px-2 py-1';
  const text = size === 'sm' ? 'text-[10px]' : 'text-xs';
  const Icon = cfg.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1 ${padding} ${text} font-semibold rounded ${cfg.cls}`}
      data-testid={testId || `approval-status-chip-${s}`}
    >
      <Icon size={size === 'sm' ? 10 : 12} /> {cfg.label}
    </span>
  );
}

/**
 * ApprovalPanel — drops into the BCB hero when a case is loaded.
 * Renders actions appropriate for the current status:
 *   draft/rejected → "Submit for approval" form (approver email + comment)
 *   submitted     → Approve / Reject buttons (any authed user)
 *   approved      → readonly success
 */
export function ApprovalPanel({ caseId, caseData, onChanged }) {
  const [approverEmail, setApproverEmail] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deciding, setDeciding] = useState(null); // 'approved' | 'rejected'

  if (!caseId || !caseData) return null;
  const status = (caseData.status || 'draft').toLowerCase();
  const token = secureStorage.getItem('session_token');

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!approverEmail.trim() || !approverEmail.includes('@')) {
      toast.error('Please enter a valid approver email');
      return;
    }
    setSubmitting(true);
    try {
      const res = await authedFetch(
        `${API_URL}/api/business-case-builder/${caseId}/submit-for-approval`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            approver_email: approverEmail.trim(),
            comment: comment.trim() || null,
          }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Submission failed');
      }
      toast.success(`Sent to ${approverEmail} for approval`);
      setComment('');
      onChanged?.();
    } catch (err) {
      toast.error(err.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecision = async (decision) => {
    setDeciding(decision);
    try {
      const res = await authedFetch(
        `${API_URL}/api/business-case-builder/${caseId}/${decision === 'approved' ? 'approve' : 'reject'}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ comment: comment.trim() || null }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Decision failed');
      }
      toast.success(decision === 'approved' ? 'Case approved' : 'Case rejected');
      setComment('');
      onChanged?.();
    } catch (err) {
      toast.error(err.message || 'Decision failed');
    } finally {
      setDeciding(null);
    }
  };

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-4"
      data-testid="approval-panel"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <ShieldCheck size={14} /> Approval &amp; sign-off
        </h3>
        <ApprovalStatusChip status={status} size="md" />
      </div>

      {status === 'approved' && (
        <div
          className="mt-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-900"
          data-testid="approval-panel-approved"
        >
          <div className="flex items-center gap-2 font-semibold">
            <CheckCircle size={14} /> Signed off
          </div>
          <p className="text-xs mt-1 text-emerald-800">
            Approved by <strong>{caseData.approved_by || 'unknown'}</strong>
            {caseData.approved_at
              ? ` on ${new Date(caseData.approved_at).toLocaleDateString()}`
              : ''}
          </p>
        </div>
      )}

      {status === 'rejected' && (
        <div
          className="mt-2 p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-900"
          data-testid="approval-panel-rejected"
        >
          <div className="flex items-center gap-2 font-semibold">
            <XCircle size={14} /> Rejected
          </div>
          <p className="text-xs mt-1 text-rose-800">
            Rejected by <strong>{caseData.rejected_by || 'unknown'}</strong>
            {caseData.rejected_at
              ? ` on ${new Date(caseData.rejected_at).toLocaleDateString()}`
              : ''}
            . You may revise and resubmit.
          </p>
        </div>
      )}

      {(status === 'draft' || status === 'rejected') && (
        <form onSubmit={handleSubmit} className="space-y-2 mt-2" data-testid="approval-submit-form">
          <label className="block text-xs text-slate-600">
            Approver email
            <input
              type="email"
              value={approverEmail}
              onChange={(e) => setApproverEmail(e.target.value)}
              placeholder="e.g. cfo@yourco.com"
              required
              className="w-full mt-1 px-2 py-1.5 text-sm rounded border border-slate-200 focus:ring-2 focus:ring-teal-300"
              data-testid="approval-approver-email-input"
            />
          </label>
          <label className="block text-xs text-slate-600">
            Note for the approver (optional)
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              placeholder="Context, urgency, executive summary…"
              className="w-full mt-1 px-2 py-1.5 text-sm rounded border border-slate-200 focus:ring-2 focus:ring-teal-300 resize-none"
              data-testid="approval-comment-input"
            />
          </label>
          <button
            type="submit"
            disabled={submitting}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-50"
            data-testid="approval-submit-btn"
          >
            {submitting ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
            {status === 'rejected' ? 'Re-submit for approval' : 'Submit for approval'}
          </button>
        </form>
      )}

      {status === 'submitted' && (
        <div className="space-y-2 mt-2" data-testid="approval-decision-form">
          <div className="text-xs text-slate-600">
            Submitted to <strong>{caseData.approver_email || 'approver'}</strong>
            {caseData.submitted_at
              ? ` on ${new Date(caseData.submitted_at).toLocaleDateString()}`
              : ''}
            . Any authenticated user can record the decision.
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            placeholder="Decision note (optional)…"
            className="w-full px-2 py-1.5 text-sm rounded border border-slate-200 focus:ring-2 focus:ring-teal-300 resize-none"
            data-testid="approval-decision-comment"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleDecision('approved')}
              disabled={!!deciding}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
              data-testid="approval-approve-btn"
            >
              {deciding === 'approved' ? (
                <Loader2 className="animate-spin" size={14} />
              ) : (
                <CheckCircle size={14} />
              )}
              Approve
            </button>
            <button
              onClick={() => handleDecision('rejected')}
              disabled={!!deciding}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 disabled:opacity-50"
              data-testid="approval-reject-btn"
            >
              {deciding === 'rejected' ? (
                <Loader2 className="animate-spin" size={14} />
              ) : (
                <XCircle size={14} />
              )}
              Reject
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ApprovalPanel;
